import type { CatalogueMarket, SyncRun } from './catalogue-client';

export interface CatalogueMarketGroup {
  readonly type: string;
  readonly markets: readonly CatalogueMarket[];
}

/** Group an Event's Markets by `marketType` (the Catalogue browse's grouping unit). */
export function groupMarketsByType(markets: readonly CatalogueMarket[]): CatalogueMarketGroup[] {
  const byType = new Map<string, CatalogueMarket[]>();
  for (const market of markets) {
    (
      byType.get(market.marketType) ?? byType.set(market.marketType, []).get(market.marketType)!
    ).push(market);
  }
  return [...byType.entries()].map(([type, groupMarkets]) => ({ type, markets: groupMarkets }));
}

/** A Market's subject (e.g. "Jake McCarthy"), the wire name minus its " - Total Bases" suffix. */
export function subjectFromMarketName(name: string): string {
  return name.split(' - ')[0] ?? name;
}

/** A quote is real only once both sides carry a price; a suspended/inactive market resolves to 0/0. */
export function hasQuote(quote: CatalogueMarket['quote']): boolean {
  return quote.over > 0 && quote.under > 0;
}

/** An Event's meta line, e.g. "MLB · 10 Jul 2026 · 20:10". */
export function formatEventMeta(competition: string | null, startTimeIso: string): string {
  const date = new Date(startTimeIso);
  const datePart = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
  const timePart = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  }).format(date);
  return `${competition ?? 'MLB'} · ${datePart} · ${timePart}`;
}

/** An Event's prop-count summary, e.g. "3 market types · 7 props" — from the Event's own counts (CAE-322). */
export function formatEventCounts(marketTypeCount: number, propCount: number): string {
  const typeWord = marketTypeCount === 1 ? 'market type' : 'market types';
  const propWord = propCount === 1 ? 'prop' : 'props';
  return `${marketTypeCount} ${typeWord} · ${propCount} ${propWord}`;
}

/** An Event's prop count once a market-state filter narrows it, e.g. "1 matching prop" (CAE-322). */
export function formatFilteredPropCount(propCount: number): string {
  const propWord = propCount === 1 ? 'prop' : 'props';
  return `${propCount} matching ${propWord}`;
}

/** The Catalogue header's last-sync time, e.g. "Last sync 2m ago" / "Last sync 3h ago". */
export function formatRelativeSync(
  iso: string | null | undefined,
  now: number = Date.now(),
): string {
  if (iso == null) return 'Not yet synced';
  const diffMs = now - new Date(iso).getTime();
  const minutes = Math.max(0, Math.round(diffMs / 60_000));
  if (minutes < 1) return 'Last sync just now';
  if (minutes < 60) return `Last sync ${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `Last sync ${hours}h ago`;
  const days = Math.round(hours / 24);
  return `Last sync ${days}d ago`;
}

/**
 * The visual tone of a sync status card (CAE-469). `idle` is the neutral pre-run state — crucially
 * NOT `success`, so a card never reads as up-to-date in success-green before any run is known.
 */
export type SyncCardTone = 'idle' | 'running' | 'success' | 'failure';

export interface SyncCardView {
  readonly title: string;
  readonly tone: SyncCardTone;
  /** The time to show under the title — the run's finish, or its start if it never finished. */
  readonly timestamp: string | null;
}

/**
 * Map a sync's latest run to its status card (CAE-469). Server-fed, so the card survives a reload
 * and reflects nightly runs. A `stalled` run — a `running` row the read layer aged out — reads as a
 * failure, an honest state rather than a permanent spinner. With no run yet, the card is neutral
 * (`idle`), never success-green.
 */
export function syncCardView(noun: string, run: SyncRun | null): SyncCardView {
  if (!run) {
    return { title: `${noun} not yet synced`, tone: 'idle', timestamp: null };
  }
  const timestamp = run.finishedAt ?? run.startedAt;
  switch (run.status) {
    case 'succeeded':
      return { title: `${noun} synced`, tone: 'success', timestamp };
    case 'failed':
      return { title: `${noun} sync failed`, tone: 'failure', timestamp };
    case 'stalled':
      return { title: `${noun} sync stalled`, tone: 'failure', timestamp };
    case 'running':
      return { title: `${noun} syncing…`, tone: 'running', timestamp };
  }
}

/** An Event's UTC calendar date as `yyyy-mm-dd`, matching `<input type="date">`'s value format. */
export function eventDateKey(startTimeIso: string): string {
  return new Date(startTimeIso).toISOString().slice(0, 10);
}

/**
 * Home/away team names from an Event title. Canonical feed form is
 * "Away Team at Home Team" (CONTEXT.md); curated UI copy also uses "vs" / "@".
 * First side is away (left crest), second is home (right crest).
 */
export function splitEventTeams(name: string): { away: string; home: string } | null {
  const parts = name.split(/\s+(?:at|vs\.?|@)\s+/i);
  if (parts.length !== 2) return null;
  const [away, home] = parts;
  return away && home ? { away: away.trim(), home: home.trim() } : null;
}
